import * as Yup from "yup";
import { RestrictedDataValidation } from "../../../../../../shared/utils/custom-validation.utils";
import { hasInvalidPartyLocationValue } from "./buyer-seller-lookup.utils";

const TE_ID_PATTERN = /^TE-\d{8}$/;
const isNaValue = (value?: string | null) =>
  typeof value === "string" && value.trim().toLowerCase() === "na";

const hasRestrictedPlaceholder = (value?: string | null, allowNa = false) => {
  if (!value) return false;
  const trimmed = value.trim();
  if (trimmed === "") return false;
  if (allowNa && isNaValue(trimmed)) return false;
  return RestrictedDataValidation.isRestrictedData(trimmed);
};

export const getBuyerSellerDetailsValidationSchema = (
  tcStatus: string,
  sellerScStatus: string,
  isReturnTc: boolean
) => {
  return Yup.object().shape({
    sellerLicenseNumber: Yup.string()
      .max(50, "Maximum 50 Characters is allowed")
      .test(
        "seller-client-placeholder",
        "Restricted placeholder values are not allowed",
        (value) => !hasRestrictedPlaceholder(value, false)
      )
      .test("seller-client-required", "Seller Client Number is required", function(value) {
        if (["R", "RA", "RI"].includes(tcStatus)) return true;
        return !!value;
      }),
    sellerCertifiedOrganizationName: Yup.string()
      .max(255, "Maximum 255 Characters is allowed")
      .test("seller-cert-org-conditional", function (value) {
        if (["R", "RA", "RI"].includes(tcStatus)) return true;
        if (["A", "I"].includes(tcStatus) && ["W", "WITHDRAWN", "E", "EXPIRED", "S", "SUSPENDED"].includes(sellerScStatus)) return true;

        if (!value || value.trim() === "") {
          return this.createError({ message: "Seller Certified Organization Name is required" });
        }
        if (hasRestrictedPlaceholder(value)) {
          return this.createError({ message: "Restricted placeholder values are not allowed" });
        }
        return true;
      }),
    sellerCountryOrArea: Yup.string().required(
      "Seller Country/Area is required"
    ).test("seller-country-placeholder", "Restricted placeholder values are not allowed", (value) => !hasRestrictedPlaceholder(value)),
    buyerLicenseNumber: Yup.string()
      .nullable()
      .test(
        "buyer-license-placeholder",
        "Restricted placeholder values are not allowed",
        (value) => !hasRestrictedPlaceholder(value, false)
      ),
    buyerScCbLicensingCode: Yup.string().when("isBuyerTeCertified", {
      is: (value: string) => value !== "No",
      then: (schema) =>
        schema
          .required("Buyer CB Licensing Code is required")
          .matches(/^CB-[A-Z]{3}$/, "Buyer CB Licensing Code must match CB-XXX"),
      otherwise: (schema) => schema.nullable(),
    }),
    sellerName: Yup.string()
      .required("Seller Name is required")
      .max(255, "Maximum 255 Characters allowed")
      .test("seller-name-placeholder", "Restricted placeholder values are not allowed", (value) => !hasRestrictedPlaceholder(value)),

    sellerTEId: Yup.string()
      .required("Seller TE ID is required")
      .matches(TE_ID_PATTERN, "Seller TE ID must match TE-########")
      .test(
        "sellerTEId-validation",
        "Custom Error: Seller TE ID must be different from Buyer TE ID",
        function (value) {
          const { buyerTEId } = this.parent;
          if (!value) return true;
          if (value === buyerTEId) {
            return this.createError({
              message: "Buyer TE ID must not be equal to Seller TE ID",
            });
          }
          return true;
        }
      ),

    sellerAddress1: Yup.string()
      .required("Seller Address Line 1 is required")
      .max(1000, "Maximum 1000 Characters is allowed")
      .test("seller-address-placeholder", "Restricted placeholder values are not allowed", (value) => !hasRestrictedPlaceholder(value)),

    sellerStateOrProvince: Yup.string()
      .required("Seller State/Province required")
      .test("seller-state-placeholder", "Restricted placeholder values are not allowed", (value) => !hasRestrictedPlaceholder(value)),

    sellerSCNumber: Yup.string()
      .required("Seller SC Number is required")
      .max(50, "Maximum 50 Characters is allowed")
      .test("seller-sc-placeholder", "Restricted placeholder values are not allowed", (value) => !hasRestrictedPlaceholder(value, false)),

    buyerName: Yup.string()
      .required("Buyer Name is required")
      .max(255, "Maximum 255 Characters is allowed")
      .test("buyer-name-placeholder", "Restricted placeholder values are not allowed", (value) => !hasRestrictedPlaceholder(value)),

    buyerTEId: Yup.string()
      .required()
      .matches(TE_ID_PATTERN, "Buyer TE ID must match TE-########")
      .test(
        "buyerTEId-validation",
        "Custom Error: Buyer TE ID must be different from Seller TE ID",
        function (value) {
          const { sellerTEId } = this.parent;
          if (!value) return true;
          if (value === sellerTEId) {
            return this.createError({
              message: "Seller TE ID must not be equal to Buyer TE ID",
            });
          }
          return true;
        }
      ),

    buyerAddress1: Yup.string()
      .required("Buyer Address Line 1 is required")
      .max(1000, "Maximum 1000 Characters is allowed")
      .test("buyer-address-placeholder", "Restricted placeholder values are not allowed", (value) => !hasRestrictedPlaceholder(value)),

    buyerStateOrProvince: Yup.string()
      .required("Buyer State/Province is required")
      .test("buyer-state-placeholder", "Restricted placeholder values are not allowed", (value) => !hasRestrictedPlaceholder(value)),

    buyerCountryOrArea: Yup.string()
      .required("Buyer Country or Area is required")
      .test("buyer-country-placeholder", "Restricted placeholder values are not allowed", (value) => !hasRestrictedPlaceholder(value)),

    nonCertifiedTrader: Yup.array().of(
      Yup.object().shape({
        sellerNonCertifiedTraderName: Yup.string()
          .nullable()
          .required("Seller non-certified trader name is required; use na if not applicable")
          .max(255, "Maximum 255 Characters is allowed")
          .test('no-restricted-data', 'N/A, Not available, n/a are not allowed. Use "na" if not applicable',
            (value) => !hasRestrictedPlaceholder(value, true)),
        sellerNonCertifiedTraderTeId: Yup.string()
          .nullable()
          .test("noncert-teid-required", "Seller non-certified trader TE-ID is required when trader name is not na", function(value) {
            const traderName = this.parent?.sellerNonCertifiedTraderName;
            if (!traderName || isNaValue(traderName)) return true;
            return !!value;
          })
          .test("noncert-teid-format", "Seller non-certified trader TE-ID must match TE-########", (value) => {
            if (!value) return true;
            return TE_ID_PATTERN.test(value);
          }),
      })
    ).min(1, "Please add Non-Certified Trader Info"),
    buyerAddress2: Yup.string().nullable(), // Optional field
    buyerAddress3: Yup.string().nullable(), // Optional field
    buyerPostcode: Yup.string()
      .required("Buyer Postcode is required")
      .test(
        "valid-party-location",
        'Whitespace-only, N/A, Not available, and null are not allowed. Use "na" if not available',
        (value) => !hasInvalidPartyLocationValue(value)
      ),
    buyerTown: Yup.string()
      .required("Buyer Town is required")
      .max(255, "Maximum 255 Characters is allowed")
      .test(
        "valid-party-location",
        'Whitespace-only, N/A, Not available, and null are not allowed. Use "na" if not available',
        (value) => !hasInvalidPartyLocationValue(value)
      ),
    sellerNonCertifiedTraderName: Yup.string()
      .nullable()
      .test('no-restricted-data', 'N/A, Not available, n/a are not allowed. Use "na" if not applicable', 
        (value) => {
          if (!value) return true;
          const trimmedValue = value.trim();
          if (trimmedValue === "na") return true;
          return !RestrictedDataValidation.isRestrictedData(value);
        }),
    SellerNonCertifiedTraderTEId: Yup.string().nullable(), // Optional field
    sellerPostcode: Yup.string()
      .required("Seller Postcode is required")
      .test(
        "valid-party-location",
        'Whitespace-only, N/A, Not available, and null are not allowed. Use "na" if not available',
        (value) => !hasInvalidPartyLocationValue(value)
      ),
    sellerTown: Yup.string()
      .required("Seller Town is required")
      .max(255, "Maximum 255 Characters is allowed")
      .test(
        "valid-party-location",
        'Whitespace-only, N/A, Not available, and null are not allowed. Use "na" if not available',
        (value) => !hasInvalidPartyLocationValue(value)
      ),
    tcSellerProductDeclarations: Yup.string().nullable(),
    buyerSCNumber: Yup.string()
      .required("Buyer SC Number is required")
      .max(50, "Maximum 50 Characters is allowed")
      .test('buyer-sc-placeholder', 'N/A, Not available, n/a are not allowed. Use "na" if not applicable',
        (value) => !hasRestrictedPlaceholder(value, true))
      .test(
        "unique-sc-number",
        "Buyer SC Number must be different from Seller SC Number",
        function(value) {
          const { sellerSCNumber } = this.parent;
          if (isNaValue(value)) return true;
          if (!value || !sellerSCNumber) return true;
          return value !== sellerSCNumber;
        }
      ),
     buyerCertifiedOrganizationName: Yup.string()
      .max(255, "Maximum 255 Characters is allowed")
      .test("buyer-cert-org-conditional", function(value) {
        if (["R", "RA", "RI"].includes(tcStatus)) return true;

        const { buyerSCNumber } = this.parent;
        if (isNaValue(buyerSCNumber)) {
          if (!isNaValue(value)) {
            return this.createError({ message: 'Buyer Certified Organization Name must be "na" when Buyer SC Number is "na"' });
          }
          return true;
        }
        if (!value || value.trim() === "") {
          return this.createError({ message: "Buyer Certified Organization Name is required" });
        }
        if (hasRestrictedPlaceholder(value)) {
          return this.createError({ message: "Restricted placeholder values are not allowed" });
        }
        return true;
      }),
  });
};
